import type { StudentIdentitySummary } from '@/components/students/StudentIdentity';
import { ApiError, fetchApi } from '@/services/api-client';

const CLASS_TESTS_PATH = '/admin/class-tests';

export type ClassTestStatus = 'scheduled' | 'cancelled';
export interface OnlineTestStatus {
  openedAt: string | null;
  closedAt: string | null;
}

export type OnlineSheetState = 'not_started' | 'in_progress' | 'to_mark' | 'complete';

export type ClassTestGradingMode = 'none' | 'total' | 'sections' | 'questions' | 'online';
export type ClassTestResultStatus = 'marked' | 'absent' | 'excused';

export interface ClassTestTeachingOption {
  subjectId: string;
  subjectName: string;
  classes: Array<{ id: string; name: string; gradeLevel: number }>;
  /** `weekday` is 1 = Monday … 5 = Friday. Every slot of the subject. */
  slots: Array<{ weekday: number; periodId: string; classIds: string[] }>;
  /** The slots that hold two or more classes at once. */
  sharedSlots: Array<{ weekday: number; periodId: string; classIds: string[] }>;
}

export interface ClassTestSummary {
  id: string;
  date: string;
  periodId: string | null;
  periodName: string | null;
  subjectId: string;
  subjectName: string;
  title: string;
  classes: Array<{ id: string; name: string }>;
  teacherName: string;
  status: ClassTestStatus;
  canManage: boolean;
  gradingMode: ClassTestGradingMode;
  resultsPublishedAt: string | null;
  /** Online tests only. */
  online?: OnlineTestStatus | null;
  /** Only on the teacher's own list, and null for an ungraded or cancelled test. */
  marking?: ClassTestMarking | null;
}

export interface ClassTestMarking {
  outOf: number | null;
  /** The most extra credit a student can add, or null when the test has none. */
  extraCredit: number | null;
  /** Students sitting the test whose result is complete: every scored part, or absent or excused. */
  marked: number;
  students: number;
}

export interface ClassTestPart {
  id: string;
  label: string;
  maxScore: number;
  extraCredit: boolean;
  sortOrder: number;
}

export interface ClassTestDetail extends ClassTestSummary {
  reviseNote: string | null;
  remindDayBefore: boolean;
  maxScore: number | null;
  extraCreditMax: number | null;
  parts: ClassTestPart[];
}

export interface ClassTestWeek {
  weekStart: string;
  tests: ClassTestSummary[];
}

export interface CreateClassTestBody {
  subjectId: string;
  classIds: string[];
  date: string;
  periodId?: string | null;
  title: string;
  reviseNote?: string | null;
  remindDayBefore: boolean;
}

export type UpdateClassTestBody = Partial<CreateClassTestBody>;

export interface ClassTestWeekQuery {
  date: string;
  classIds?: string[];
  excludeId?: string;
}

export interface ClassTestMineQuery {
  from?: string;
  to?: string;
}

export type ClassTestHistoryScope = 'mine' | 'all';

export interface ClassTestHistoryQuery {
  /** `all` is the SST's whole-school view; `mine` the subjects the caller teaches. */
  scope: ClassTestHistoryScope;
  schoolYearId?: string;
  subjectId?: string;
  classId?: string;
  studentId?: string;
  page?: number;
  pageSize?: number;
}

export type LetterBucket = 'A' | 'B' | 'C' | 'D' | 'F';

export interface ClassTestHistoryStats {
  average: number | null;
  highest: number | null;
  lowest: number | null;
  letters: Record<LetterBucket, number>;
}

export interface ClassTestResultPartLine {
  label: string;
  score: number;
  max: number;
  extraCredit: boolean;
}

export interface ClassTestHistoryResult {
  studentId: string;
  studentName: string;
  student: StudentIdentitySummary;
  className: string;
  status: ClassTestResultStatus;
  total: number | null;
  percent: number | null;
  letter: string | null;
  comment: string | null;
  parts: ClassTestResultPartLine[];
}

export interface ClassTestHistoryTest extends ClassTestSummary {
  max: number | null;
  extraCredit: number | null;
  resultsPublished: boolean;
  /** Null until results are published. */
  stats: ClassTestHistoryStats | null;
  results: ClassTestHistoryResult[] | null;
}

export interface ClassTestHistoryPage {
  items: ClassTestHistoryTest[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ClassTestHistoryFilters {
  schoolYearId: string | null;
  schoolYears: Array<{ id: string; name: string; active: boolean }>;
  subjects: Array<{ id: string; name: string }>;
  classes: Array<{ id: string; name: string }>;
  students: Array<{ id: string; name: string; className: string }>;
}

export interface ClassTestSetupBody {
  gradingMode: ClassTestGradingMode;
  maxScore?: number | null;
  extraCreditMax?: number | null;
  /** A part with an `id` keeps its scores; one without is new. */
  parts?: Array<{ id?: string; label: string; maxScore: number; extraCredit: boolean }>;
}

export interface LetterBand {
  label: string;
  percentMin: number;
  percentMax: number;
  rank: number;
}

export interface MarkSheetRow {
  studentId: string;
  studentName: string;
  student: StudentIdentitySummary;
  className: string;
  status: ClassTestResultStatus | null;
  /** Keyed by part id, or `total` for a total-mode test. */
  scores: Record<string, number>;
  extraScore: number | null;
  total: number | null;
  percent: number | null;
  letter: string | null;
  comment: string | null;
  leftClass: boolean;
  /** Online tests only. */
  onlineState?: OnlineSheetState;
  /** Online tests only: free-text answers still waiting for a mark. */
  toMark?: number;
}

export interface MarkSheet {
  test: ClassTestDetail;
  max: number | null;
  rows: MarkSheetRow[];
  stats: { average: number | null; highest: number | null; lowest: number | null };
  bands: LetterBand[];
}

export interface SaveMarkSheetRow {
  studentId: string;
  /** null clears the student's row. */
  status: ClassTestResultStatus | null;
  scores?: Record<string, number>;
  extraScore?: number | null;
  comment?: string | null;
}

/** `partId` is a part id, `total` or `extra` in total mode, or null for a row-level problem. */
export interface MarkSheetCellError {
  studentId: string;
  partId: string | null;
  message: string;
}

/** The per-cell problems behind a rejected sheet save, or null when the failure was something else. */
export function markSheetErrorsOf(error: unknown): MarkSheetCellError[] | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const errors = (error.body as { errors?: unknown } | undefined)?.errors;
  return Array.isArray(errors) ? (errors as MarkSheetCellError[]) : null;
}

/** The ids of students still without a result when publishing was refused, or null otherwise. */
export function publishMissingOf(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const missing = (error.body as { missing?: unknown } | undefined)?.missing;
  return Array.isArray(missing) ? (missing as string[]) : null;
}

function query(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

const grading = (id: string) => `${CLASS_TESTS_PATH}/${id}/grading`;

export const classTestsApi = {
  options: () => fetchApi<ClassTestTeachingOption[]>(`${CLASS_TESTS_PATH}/options`),
  week: ({ date, classIds, excludeId }: ClassTestWeekQuery) =>
    fetchApi<ClassTestWeek>(
      `${CLASS_TESTS_PATH}/week${query({ date, classIds: classIds?.length ? classIds.join(',') : undefined, excludeId })}`
    ),
  mine: (q: ClassTestMineQuery = {}) =>
    fetchApi<ClassTestSummary[]>(`${CLASS_TESTS_PATH}/mine${query({ from: q.from, to: q.to })}`),
  history: ({ page, pageSize, ...q }: ClassTestHistoryQuery) =>
    fetchApi<ClassTestHistoryPage>(
      `${CLASS_TESTS_PATH}/history${query({ ...q, page: page ? String(page) : undefined, pageSize: pageSize ? String(pageSize) : undefined })}`
    ),
  historyFilters: (q: Pick<ClassTestHistoryQuery, 'scope' | 'schoolYearId'>) =>
    fetchApi<ClassTestHistoryFilters>(`${CLASS_TESTS_PATH}/history/filters${query(q)}`),
  create: (body: CreateClassTestBody) =>
    fetchApi<ClassTestDetail>(CLASS_TESTS_PATH, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: UpdateClassTestBody) =>
    fetchApi<ClassTestDetail>(`${CLASS_TESTS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  cancel: (id: string) => fetchApi<ClassTestDetail>(`${CLASS_TESTS_PATH}/${id}/cancel`, { method: 'POST' }),
  setup: (id: string, body: ClassTestSetupBody) =>
    fetchApi<ClassTestDetail>(`${grading(id)}/setup`, { method: 'PUT', body: JSON.stringify(body) }),
  sheet: (id: string) => fetchApi<MarkSheet>(`${grading(id)}/sheet`),
  saveSheet: (id: string, rows: SaveMarkSheetRow[]) =>
    fetchApi<MarkSheet>(`${grading(id)}/sheet`, { method: 'PUT', body: JSON.stringify({ rows }) }),
  publish: (id: string) => fetchApi<MarkSheet>(`${grading(id)}/publish`, { method: 'POST' }),
  unpublish: (id: string) => fetchApi<MarkSheet>(`${grading(id)}/unpublish`, { method: 'POST' }),
};
